import React, { useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';

interface VoiceWaveformProps {
    isActive: boolean;
    className?: string;
    barCount?: number;
    barColor?: string;
    barActiveColor?: string;
    /** 외부에서 전달받는 MediaStream (Cloud STT 모드용) */
    audioStream?: MediaStream | null;
}

/**
 * 실시간 마이크 입력 파형 시각화 컴포넌트
 *
 * - audioStream이 전달되면: 해당 스트림의 오디오 데이터로 시각화
 * - audioStream이 없으면: 자체적으로 getUserMedia()로 마이크 접근
 */
const VoiceWaveform: React.FC<VoiceWaveformProps> = ({
    isActive,
    className,
    barCount = 32,
    barColor = '#e5e7eb',
    barActiveColor = '#8b5cf6',
    audioStream,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animFrameRef = useRef<number>(0);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const ownStreamRef = useRef<MediaStream | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);

    useEffect(() => {
        if (isActive) {
            startVisualization();
        } else {
            stopVisualization();
            drawIdle();
        }
        return () => stopVisualization();
    }, [isActive, audioStream]);

    // 초기 idle 그리기
    useEffect(() => {
        drawIdle();
    }, []);

    const startVisualization = async () => {
        try {
            let stream: MediaStream;

            if (audioStream) {
                // 외부에서 전달받은 스트림 사용 (마이크 중복 접근 안 함)
                stream = audioStream;
            } else {
                // 자체 마이크 접근
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                ownStreamRef.current = stream;
            }

            const audioCtx = new AudioContext();
            audioCtxRef.current = audioCtx;

            const source = audioCtx.createMediaStreamSource(stream);
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 128;
            analyser.smoothingTimeConstant = 0.7;
            source.connect(analyser);
            analyserRef.current = analyser;

            draw();
        } catch (err: any) {
            if (err?.name === 'NotAllowedError') {
                console.info('[Waveform] 마이크 권한이 허용되지 않았습니다.');
            } else {
                console.warn('[Waveform] Microphone access failed:', err);
            }
        }
    };

    const stopVisualization = () => {
        if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = 0;
        }
        // 자체 생성한 스트림만 정지 (외부 스트림은 건드리지 않음)
        if (ownStreamRef.current) {
            ownStreamRef.current.getTracks().forEach(t => t.stop());
            ownStreamRef.current = null;
        }
        if (audioCtxRef.current) {
            audioCtxRef.current.close();
            audioCtxRef.current = null;
        }
        analyserRef.current = null;
    };

    const draw = () => {
        const canvas = canvasRef.current;
        const analyser = analyserRef.current;
        if (!canvas || !analyser) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);

        const w = canvas.width;
        const h = canvas.height;
        ctx.clearRect(0, 0, w, h);

        const gap = 2;
        const barWidth = (w - (barCount - 1) * gap) / barCount;
        const step = Math.max(1, Math.floor(bufferLength / barCount));

        for (let i = 0; i < barCount; i++) {
            const dataIndex = Math.min(i * step, bufferLength - 1);
            const value = dataArray[dataIndex] / 255;
            const barHeight = Math.max(3, value * h * 0.9);
            const x = i * (barWidth + gap);
            const y = (h - barHeight) / 2;

            ctx.fillStyle = barActiveColor;
            ctx.beginPath();
            ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2);
            ctx.fill();
        }

        animFrameRef.current = requestAnimationFrame(draw);
    };

    const drawIdle = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const w = canvas.width;
        const h = canvas.height;
        ctx.clearRect(0, 0, w, h);

        const gap = 2;
        const barWidth = (w - (barCount - 1) * gap) / barCount;

        for (let i = 0; i < barCount; i++) {
            const x = i * (barWidth + gap);
            ctx.fillStyle = barColor;
            ctx.beginPath();
            ctx.roundRect(x, (h - 3) / 2, barWidth, 3, barWidth / 2);
            ctx.fill();
        }
    };

    return (
        <canvas
            ref={canvasRef}
            width={320}
            height={60}
            className={cn("w-full max-w-xs h-[60px]", className)}
        />
    );
};

export default VoiceWaveform;
